import { Component, OnDestroy, OnInit, ViewChild, Input } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Event } from '../../model/event';
import { Track } from '../../model/track';
import { Form } from 'src/app/model/form';
import { ActivatedRoute, Router } from '@angular/router';
import { EventsService } from 'src/app/service/events.service';
import { Location } from '@angular/common';
import { Error } from 'src/app/model/error';
import { Submission, SubmissionStatus } from 'src/app/model/paper';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { Answer } from 'src/app/model/answer';
import { NavbarService } from 'src/app/service/navbar.service';
import { User } from 'src/app/model/user';
import { InfoService } from 'src/app/service/info.service';
import { NotificationService } from 'src/app/service/notification.service';
import { AuthService } from 'src/app/service/auth.service';
import { EventSubmissionStep } from 'src/app/model/eventSubmissionStep';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatStepper } from '@angular/material/stepper';
import { Subscription } from 'rxjs';
import { AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-create-submission',
  templateUrl: './create-submission.component.html',
  styleUrls: ['./create-submission.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: {
        showError: true
      }
    }
  ]
})
export class CreateSubmissionComponent implements OnInit, OnDestroy {
  // Setter porque o `#stepper` fica dentro de `*ngIf` — só resolve
  // depois que `event/track/verifyForm` chegam. Ao ficar disponível,
  // ligamos um listener em `selectionChange` que corrige a altura do
  // container ANTES da animação de transform começar a pintar.
  //
  // Contexto do bug (Safari + Material 11 horizontal stepper):
  //   O `.mat-horizontal-content-container` tem `overflow: hidden`;
  //   cada step é animado via `transform: translateX(...)` pelo
  //   `@angular/animations`. Em Chrome, Angular re-computa e commita
  //   a nova altura do container ANTES da rAF em que o transform
  //   começa a pintar → conteúdo desliza dentro da altura correta.
  //   Em Safari, o pipeline de animação do WebKit despacha o
  //   transform em uma etapa que roda ANTES do commit da altura
  //   feito pelo Angular → o conteúdo entra clipado e só depois a
  //   caixa "salta" pra altura certa.
  //
  // Fix: no `selectionChange`, medimos SÍNCRONO o `scrollHeight` do
  // novo step e setamos `container.style.height` já com o valor
  // final. `container.offsetHeight` força o WebKit a flushar layout
  // no exato momento → quando a rAF do transform roda em seguida,
  // a altura já está aplicada.
  public stepper: MatStepper;
  private _stepperSelSub?: Subscription;
  private _stepperContentResizeObs?: ResizeObserver;
  @ViewChild('stepper')
  set stepperRef(s: MatStepper) {
    this.stepper = s;
    this._stepperSelSub?.unsubscribe();
    this._stepperContentResizeObs?.disconnect();
    if (s) {
      // 1) Cada troca de etapa: mede o novo step e commita altura
      //    antes do transform pintar (fix da entrada — Safari só).
      // 2) Re-observa o novo step ativo com `ResizeObserver` — assim
      //    a altura acompanha DENTRO da mesma etapa quando o conteúdo
      //    cresce (ex.: adicionar autor no step 2). Sem isso o container
      //    trava na altura setada em (1) e a nova linha fica clipada.
      this._stepperSelSub = s.selectionChange.subscribe(ev => {
        this.applyStepperContainerHeight(s, ev.selectedIndex);
        this.observeActiveStepResize(s, ev.selectedIndex);
      });
      // Observação inicial: `selectionChange` só emite em trocas — pra
      // acompanhar crescimento do step 0 na carga precisa disparar já.
      this.observeActiveStepResize(s, s.selectedIndex ?? 0);
    }
  }

  private applyStepperContainerHeight(stepper: MatStepper, selectedIndex: number): void {
    // `Promise.resolve().then(...)` roda após o CD atual (que aplica
    // `aria-hidden` no step novo/velho), mas ainda antes do rAF que
    // o pipeline de animações vai usar pra pintar o transform.
    Promise.resolve().then(() => {
      const host = (stepper as any)?._elementRef?.nativeElement as HTMLElement | undefined;
      if (!host) { return; }
      const container = host.querySelector('.mat-horizontal-content-container') as HTMLElement | null;
      if (!container) { return; }
      const contents = host.querySelectorAll('.mat-horizontal-stepper-content');
      const nextContent = contents[selectedIndex] as HTMLElement | undefined;
      if (!nextContent) { return; }
      const h = nextContent.scrollHeight;
      if (h > 0) {
        container.style.height = `${h}px`;
        // Read forces layout flush no WebKit — garante que a nova
        // altura entra em vigor ANTES do transform pintar.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        container.offsetHeight;
      }
    });
  }

  private observeActiveStepResize(stepper: MatStepper, selectedIndex: number): void {
    // Desliga observer anterior e liga no step ativo. `ResizeObserver`
    // avisa quando o `scrollHeight` do conteúdo do step muda (usuário
    // adicionou autor, expandiu bloco, etc.) — atualizamos a altura
    // do container em cima disso.
    this._stepperContentResizeObs?.disconnect();
    if (typeof ResizeObserver === 'undefined') { return; }
    Promise.resolve().then(() => {
      const host = (stepper as any)?._elementRef?.nativeElement as HTMLElement | undefined;
      if (!host) { return; }
      const container = host.querySelector('.mat-horizontal-content-container') as HTMLElement | null;
      const contents = host.querySelectorAll('.mat-horizontal-stepper-content');
      const activeContent = contents[selectedIndex] as HTMLElement | undefined;
      if (!container || !activeContent) { return; }
      this._stepperContentResizeObs = new ResizeObserver(() => {
        const h = activeContent.scrollHeight;
        if (h > 0) { container.style.height = `${h}px`; }
      });
      this._stepperContentResizeObs.observe(activeContent);
    });
  }

  event: Event;
  track: Track;
  createSubmissionForm: FormGroup;

  verifyForm = false;

  error: Error;

  params: {
    eventId: number,
    trackId: number
  };

  title = 'submissions.create.title';

  submission: Submission;
  @Input() isEditing = false;
  @Input() includeLoggedUser = true;
  @Input() isChairView = false;

  isChair = false;

  hasError = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    protected eventService: EventsService,
    protected location: Location,
    private submissionsService: SubmissionsService,
    private navbarService: NavbarService,
    private notificationService: NotificationService,
    private router: Router,
    private infoService: InfoService,
    private authService: AuthService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.hasError = false;
    setTimeout(() => {
      this.navbarService.loadingStart();

      this.route.params.subscribe(params => {
        if (params.submissionId) {
          this.submissionsService.getSubmission(params.submissionId).subscribe(
            submission => {
              this.eventService.getEventByTrack(submission.trackID).subscribe(
                event => {
                  // Check if user is allowed
                  if (!submission.authors.find(author => author.user.id === this.infoService.user.id)) {
                    this.authService.userIsAdminOrChair(event.id).subscribe(isAdminOrChair => {
                      this.isChair = isAdminOrChair;

                      if (!isAdminOrChair) {
                        this.router.navigate(['/401']);
                      }
                    });
                  }

                  this.submission = submission;
                  this.params = {
                    eventId: event.id,
                    trackId: submission.trackID
                  };

                  this.title = 'submissions.edit.title';

                  this.getEvent();
                  this.initForm();
                },
                () => this.handleInitError(),
              );
            },
            () => this.handleInitError(),
          );
        } else {
          this.params = {
            eventId: parseInt(params.eventId, 10),
            trackId: parseInt(params.trackId, 10)
          };

          if (!this.params.eventId) {
            this.eventService.getEventByTrack(this.params.trackId).subscribe(
              event => {
                this.params.eventId = event.id;

                this.getEvent();
                this.initForm();
              },
              () => this.handleInitError(),
            );
          } else {
            this.getEvent();
            this.initForm();
          }
        }
      });
    });
  }

  /**
   * Encerra o loader e mostra estado de erro genérico quando a cadeia
   * de fetches do init falha. Sem isso, um 5xx/timeout no backend deixa
   * o spinner de `navbarService.loadingStart()` ligado indefinidamente
   * e o usuário achando que o app travou.
   */
  private handleInitError(): void {
    this.navbarService.loadingStop();
    this.error = new Error('errors.catastrophe');
    this.notificationService.notifyError('errors.catastrophe');
  }

  getEvent() {
    this.eventService.getEvent(this.params.eventId).subscribe(
      (event) => {
      this.event = event;

      let newEventSubmissionStep: EventSubmissionStep[] = [];
      let topicsStep: EventSubmissionStep;
      let formStep: EventSubmissionStep;

      for (let step of this.event.eventSubmissionStep) {
        const stepName = step.submissionStep.name;
        if (stepName == 'form') {
          formStep = step;
        } else if (stepName == 'topics') {
          topicsStep = step;
        } else if (stepName != 'files') {
          newEventSubmissionStep.push(step);
        }
      }
      newEventSubmissionStep.push(topicsStep);
      if (this.event.formSubmission != null) {
        newEventSubmissionStep.push(formStep);
      }
      this.event.eventSubmissionStep = newEventSubmissionStep;

      this.eventService.getFormSubmission(event.id, event.formSubmission)
        .subscribe(
          form => {
            this.verifyForm = true;
          },
          err => {
            const found = this.event.eventSubmissionStep.findIndex(step => step.name === 'form');
            if (found >= 0) {
              this.event.eventSubmissionStep.splice(found, 1);
            }
            this.verifyForm = true;
          });

      this.authService.userIsAdminOrChair(event.id).subscribe(isAdminOrChair =>
        this.isChair = isAdminOrChair
      );

      this.track = this.event.tracks.find(e => e.id === this.params.trackId);
      if (!this.track) {
        this.error = new Error('errors.track-not-in-event');
      }

      this.navbarService.loadingStop();
    },
      () => this.handleInitError(),
    );
  }

  submit({ valid, formAnswers }: { valid: boolean, formAnswers: Array<Answer> }): void {
    // Default values, when creating submission
    let notifyStartMessage = 'submissions.create.creating';
    let notifyStartMessageParams = {};
    let notifySuccessMessage = 'submissions.create.created';
    let previousStatus = SubmissionStatus.PENDING;

    if (this.isEditing) {
      // Override values, if editing submission
      notifyStartMessage = 'submissions.edit.editing';
      notifyStartMessageParams = { id: this.submission.id };
      notifySuccessMessage = 'submissions.edit.edited';
      previousStatus = this.submission.status;
    }

    if (valid) {
      this.notificationService.notify(notifyStartMessage, { params: notifyStartMessageParams });

      const form = this.createSubmissionForm.value;
      const temp = new Submission();
      temp.updateData({
        status: previousStatus,
        title: form.info.title,
        abstract: form.info.abstract,
        topics: form.topics.list,
        track: this.track,
        authors: form.authors.list.map((user: User, order: number) => ({ user, order })),
        formAnswers
      });

      const obs = this.isEditing ?
        this.submissionsService.editSubmission(temp.data(), this.submission.id) :
        this.submissionsService.createSubmission(temp.data());

      obs.subscribe(submission => {
        // Upload de arquivos NÃO acontece no assistente — o autor faz
        // pela página convencional da submissão (ver `getEvent()`,
        // onde o step `files` é filtrado dos steps do wizard). Aqui só
        // notificamos sucesso do registro, navegamos, e mostramos o
        // dialog de "próximos passos".
        this.notificationService.notify(notifySuccessMessage, { params: { id: submission.id } });
        this.router.navigate(
          [this.isChairView ? `/admin/${this.event.id}/submissions` : '/submissions', submission.id],
          { queryParams: { refreshCache: true, submissionCreation: true } }
        ).then(() => {
          this.dialog.open(ConfirmationDialogComponent, {
            data: {
              title: 'submissions.create.creation-dialog.title',
              content: 'submissions.create.creation-dialog.content',
              confirm: 'submissions.create.creation-dialog.button',
              params: { onlyOkButton: true, isSuccessInfo: true }
            },
          });
        });
      });
    }
  }

  get editableSubmission(){
    return (this.track?.editPendingSubmissions && this.submission?.status == SubmissionStatus.PENDING) ||
      (this.track?.editActiveSubmissions && this.submission?.status == SubmissionStatus.ACTIVE) ||
      (this.track?.editRejectedSubmissions && this.submission?.status == SubmissionStatus.REJECTED) ||
      (this.track?.editAcceptedSubmissions && this.submission?.status == SubmissionStatus.ACCEPTED);
  }

  private initForm(): void {
    this.createSubmissionForm = this.fb.group({});
    this.createSubmissionForm.setValidators(this.allControlsValid);
  }

  get eventName(): string {
    return this.event ? this.event.name : undefined;
  }

  get trackName(): string {
    if (this.track) {
      return this.track.name;
    }

    if (this.params.trackId) {
      return this.params.trackId.toString();
    }

    return undefined;
  }

  allControlsValid(): ValidatorFn {
    return (group: FormGroup): ValidationErrors => {
      const invalid = Object.keys(group.controls).some(name => !group.controls[name].valid);
      return invalid ? { invalid: 'invalid form' } : null;
    };
  }

  processNext(matStepper: MatStepper) {
    matStepper.next();
  }

  ngOnDestroy(): void {
    this._stepperSelSub?.unsubscribe();
    this._stepperContentResizeObs?.disconnect();
  }
}
